import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { log } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MB = 1024 * 1024;

/**
 * useArtworkUpload — encapsulates the Page 2 artwork upload pipeline so
 * the same handler can be wired into multiple drop targets without
 * duplicating the size-validation / 401-stale-session / auth-retry
 * logic.
 *
 * Originally lived inline in `Page2ArtworkSection` (iter95) but was
 * lifted into this hook in iter114p so the merged-into-mockup
 * dropzone (`MockupIdleDropzone`) and the inline "selected-file" UI
 * could share one source of truth.
 *
 * Returns:
 *   - `handleFile(file)` — primary entry point. Validates size and
 *     either uploads immediately (authenticated) or stashes the File
 *     and triggers the guest-auth banner (anonymous users).
 *   - `uploading`, `error` — for surfacing upload state in the UI.
 *   - `clear()` — resets file + id + error on the "remove artwork" button.
 */
export const useArtworkUpload = ({
  artworkFile,
  setArtworkFile,
  artworkId,
  setArtworkId,
  isAuthenticated,
  onRequireAuth,
  refreshAuth,
  maxFileMb = 200,
}) => {
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Track which File we already auto-retried so a second auth flip
  // doesn't re-fire an upload that's already mid-flight.
  const retriedFileRef = useRef(null);

  const uploadToStaging = async (file) => {
    setUploading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const resp = await axios.post(
        `${API_URL}/api/files/artwork/staging-upload`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setArtworkId(resp.data.artwork_id);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Stale-session handling: React tree thought we were
        // authenticated but the backend cookie has expired. Re-sync
        // AuthContext and reveal the friendly auth banner / guest
        // prompt instead of dumping the raw "Not authenticated"
        // string into the UI. Keep `artworkFile` in state so the
        // auto-retry effect re-uploads once auth flips back.
        setArtworkId(null);
        setError(null);
        try {
          await refreshAuth?.();
        } catch (refreshErr) {
          log.debug('[useArtworkUpload] refreshAuth threw:', refreshErr?.name || refreshErr);
        }
        onRequireAuth?.();
        return;
      }
      const msg = err?.response?.data?.detail || 'Upload failed — please try again.';
      setError(typeof msg === 'string' ? msg : 'Upload failed.');
      setArtworkFile(null);
      setArtworkId(null);
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    if (file.size > maxFileMb * MB) {
      setError(`File too large — max ${maxFileMb}MB.`);
      return;
    }
    setError(null);
    setArtworkFile(file);
    if (!isAuthenticated) {
      onRequireAuth?.();
      return;
    }
    uploadToStaging(file);
  };

  // Auto-retry the staging upload once the user becomes authenticated.
  // Fires only when a File is present but no artworkId has been minted
  // yet (i.e., the user picked the file while anonymous).
  useEffect(() => {
    if (
      isAuthenticated
      && artworkFile
      && !artworkId
      && !uploading
      && retriedFileRef.current !== artworkFile
    ) {
      retriedFileRef.current = artworkFile;
      uploadToStaging(artworkFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, artworkFile, artworkId]);

  const clear = () => {
    setArtworkFile(null);
    setArtworkId(null);
    setError(null);
    retriedFileRef.current = null;
  };

  return { handleFile, uploading, error, clear };
};
